import {
  collectionDoc,
  collectionRef,
} from "@/api/collectionRef/collectionOperations";
import { Button, Spinner, Tooltip } from "@radix-ui/themes";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { createToast } from "@/utils/toaster/createToast";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { participateInChallenge } from "../../helperFunctions/participateUserIntoChallenge";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { singleChallengeProps } from "../../utils/singleChallengeProps";
import { Games } from "@/models/database/games/games.model";
import { siteRoutes } from "@/routes/siteRoutesList";
import i18next from "i18next";

interface ChallengeCardActionsProps {
  slug: string;
  name: string;
  isCurrUserParticipant?: boolean;
  gameType: string;
  challengeType: singleChallengeProps["challengeData"]["type"];
  status: singleChallengeProps["status"];
}

export const ChallengeCardActions: FC<ChallengeCardActionsProps> = ({
  slug,
  name,
  gameType,
  challengeType,
  status,
}): JSX.Element => {
  const navigate = useNavigate();
  const currentUser = useCurrentUserProfile((s) => s.user);
  const [gameData, setGameData] = useState<Games>();

  const [isParticipant, setIsParticipant] = useState<boolean | undefined>(
    false
  );

  // async function getUserChallData(uid, slug) {
  //   try {
  //     await collectionDoc.junctionProfileChallenge(`${uid}_${slug}`);

  //     setIsParticipant(true);
  //   } catch (error) {
  //     console.error("Error fetching user challenge data:", error);
  //     setIsParticipant(false);
  //   }
  // }

  // useEffect(() => {
  //   if (currentUser.uid && slug) {
  //     getUserChallData(currentUser.uid, slug);
  //   }
  // }, []);

  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Get game info
  const getGameInfo = async () => {
    const games = collectionRef.games;

    try {
      const gamesRes = await games.where("value", "==", gameType).get();

      if (gamesRes && gamesRes.docs.length > 0 && gamesRes.docs[0].data()) {
        setLoading(false);
        setGameData(gamesRes.docs[0].data());
      } else {
        setLoading(false);
        setGameData(null);
      }
    } catch (error) {
      setLoading(false);
      setGameData(null);
      console.error("Error fetching game info:", error);
    }
  };

  // Check if user is already participating in this challenge
  const getJunctionProfileChallenge = async () => {
    try {
      const junctionRef = await collectionDoc
        .junctionProfileChallenge(`${currentUser.uid}_${slug}`)
        .then((doc) => doc.data());
      if (junctionRef) {
        setIsParticipant(true);
      }
    } catch (error) {
      console.error("Error fetching junctionProfileChallenge:", error);
    }
  };

  useEffect(() => {
    getGameInfo();
    getJunctionProfileChallenge();
  }, []);

  const participateCurrentUserIntoThatChallenge = async () => {
    if (!gameData) {
      return createToast({
        type: "error",
        message: "Could not find game info, please try again later",
      });
    }

    setLoading(true);
    if (!currentUser.uid) {
      navigate(siteRoutes["signUp1"]);
      return;
    }

    if (isParticipant) {
      setLoading(false);
      createToast({
        type: "info",
        message: `Already joined \`${name}\``,
      });
      return;
    }

    const res = await participateInChallenge(
      navigate,
      challengeType,
      gameData,
      currentUser.uid,
      slug,
      name
    );
    if (res) {
      setIsParticipant(true);
      // getUserChallData();
      setLoading(false);
    } else {
      setShow(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gameData && !isParticipant && brand.autoJoinChallenge) {
      participateCurrentUserIntoThatChallenge();
    }
  }, [gameData]);

  const userJoined = !gameData?.isManualSubmission && isParticipant;

  const isFinished = status === "finished";
  const isUpcoming = status === "upcoming";

  if (gameData?.isManualSubmission) {
    return (
      <Tooltip content="This challenge is manual submission only.">
        <Button
          variant="solid"
          onClick={() =>
            navigate(`${siteRoutes["submitScoreAndSlug"]}${slug}`, {
              state: {
                orgSlug: slug,
              },
            })
          }
          style={{ width: "100%" }}
        >
          {i18next.t("challenge.buttons.submit")}
        </Button>
      </Tooltip>
    );
  }

  if (isFinished) {
    return (
      <Tooltip content={i18next.t("challenges.tooltips.finished")}>
        <Button variant="solid" disabled style={{ width: "100%" }}>
          {i18next.t("challenges.status.finished")}
        </Button>
      </Tooltip>
    );
  }

  if (isUpcoming) {
    return (
      <Tooltip content={i18next.t("challenges.tooltips.upcoming")}>
        <Button variant="solid" disabled style={{ width: "100%" }}>
          {i18next.t("challenges.tooltips.upcoming")}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      content={
        userJoined
          ? "You are currently participating in this challenge."
          : "Press the button to join this challenge."
      }
    >
      <Button
        variant="solid"
        onClick={participateCurrentUserIntoThatChallenge}
        disabled={userJoined}
        style={{ width: "100%" }}
      >
        {!loading && (
          <>
            {!isParticipant && i18next.t("challenge.buttons.joinThisChallenge")}

            {userJoined && i18next.t("challenge.buttons.joined")}
            {gameData?.isManualSubmission &&
              i18next.t("challenge.buttons.submit")}
          </>
        )}

        {loading && <Spinner />}
      </Button>
    </Tooltip>
  );
};
